import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { FullScreenSpinner, useToastController } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    emptyBill,
    mapPartialBillToBill,
    transformReviewBillFromServer,
} from '../../common/utils/billUtils';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useCreateBill from '../hooks/useCreateBill';
import useGetBillDiscoveryReview from '../hooks/useGetBillDiscoveryReview';
import { billsToReviewAtom } from '../state/BillsAtoms';
import type { IBill } from '../types/Bill';
import BillDetailsEditor from './BillDetailsEditor/BillDetailsEditor';

const ManualReviewBill = () => {
    LogRocketFacade.tagPage('ManualReviewBill');
    const toast = useToastController();

    const user = useAuthedUser();
    const storeBillsToReview = useSetAtom(billsToReviewAtom);
    const { mutate: createBill, isPending } = useCreateBill();

    const backToBillsOverview = () => {
        router.navigate('/bills/bills-discovery-review');
    };

    const { refetch: refetchBills } = useGetBillDiscoveryReview(
        user.membership?.id ?? '',
    );

    const onSubmit = (newBill: IBill) => {
        const {
            accountId,
            name,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
        } = newBill;
        createBill(
            {
                urlParams: {
                    membershipId: user.membership?.id ?? '',
                },
                body: {
                    accountId,
                    name,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                },
            },
            {
                onSuccess: (newBillFromServer) => {
                    toast.show(`${payee} bill added`);

                    const transformedBill = transformReviewBillFromServer(
                        mapPartialBillToBill(newBillFromServer),
                    );

                    storeBillsToReview((prev) => {
                        if (!prev.length) return [transformedBill];
                        return prev.concat(transformedBill);
                    });

                    void refetchBills();
                    backToBillsOverview();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    if (isPending) {
        return <FullScreenSpinner />;
    }

    return (
        <BillDetailsEditor
            bill={emptyBill()}
            title="Add a bill"
            submitButtonLabel="Add"
            onSubmit={onSubmit}
            onBack={backToBillsOverview}
        />
    );
};

export default ManualReviewBill;
